/**
 * Composable store domain (products & cart).
 *
 * staleTime: 30 detik (catalog product) / 0 detik untuk cart (selalu fresh).
 */
import { computed, unref } from "@vue/composition-api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { keys } from "@/queries/keys";
import {
  fetchProducts,
  fetchProductById,
  fetchStoreProducts,
  fetchStoreOrders,
  createProduct,
  updateProduct,
  deleteProduct,
  fetchCart,
  createCartItem,
  updateCartItem,
  decrementCartItem,
  removeCartItem,
} from "@/queries/api/store";

const PRODUCT_STALE = 30 * 1000;

function p(input) {
  return unref(input);
}

/* ============================================================
 * Products
 * ============================================================ */

export function useProducts(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.store.products(p(params))),
    queryFn: () => fetchProducts(p(params)),
    staleTime: PRODUCT_STALE,
    ...options,
  });
}

export function useProductDetail(id, options = {}) {
  return useQuery({
    queryKey: computed(() => [...keys.store.all, "product", "detail", p(id)]),
    queryFn: () => fetchProductById(p(id)),
    staleTime: PRODUCT_STALE,
    enabled: computed(() => !!p(id)),
    ...options,
  });
}

/* Store Products list (params reaktif). Memakai key `keys.store.products`
   sehingga invalidasi by-product otomatis terbawa. */
export function useStoreProducts(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.store.products(p(params))),
    queryFn: () => fetchStoreProducts(p(params)),
    staleTime: PRODUCT_STALE,
    ...options,
  });
}

/* Store Orders list (transaksi /api/v1/orders). */
export function useStoreOrders(params = {}, options = {}) {
  return useQuery({
    queryKey: computed(() => keys.store.orders(p(params))),
    queryFn: () => fetchStoreOrders(p(params)),
    staleTime: PRODUCT_STALE,
    ...options,
  });
}

function invalidateProducts(qc) {
  return qc.invalidateQueries({
    queryKey: [...keys.store.all, "products"],
  });
}

export function useCreateProduct(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createProduct,
    onSuccess: () => invalidateProducts(qc),
    ...options,
  });
}

export function useUpdateProduct(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateProduct,
    onSuccess: () => invalidateProducts(qc),
    ...options,
  });
}

export function useDeleteProduct(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => invalidateProducts(qc),
    ...options,
  });
}

/* ============================================================
 * Cart — selalu fresh (staleTime: 0)
 * ============================================================ */

const CART_KEY = [...keys.store.all, "cart"];

export function useCart(options = {}) {
  return useQuery({
    queryKey: CART_KEY,
    queryFn: fetchCart,
    staleTime: 0,
    ...options,
  });
}

function invalidateCart(qc) {
  return qc.invalidateQueries({ queryKey: CART_KEY });
}

export function useCreateCartItem(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createCartItem,
    onSuccess: () => invalidateCart(qc),
    ...options,
  });
}

export function useUpdateCartItem(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateCartItem,
    onSuccess: () => invalidateCart(qc),
    ...options,
  });
}

export function useDecrementCartItem(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: decrementCartItem,
    onSuccess: () => invalidateCart(qc),
    ...options,
  });
}

export function useRemoveCartItem(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: removeCartItem,
    onSuccess: () => invalidateCart(qc),
    ...options,
  });
}
